import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { CalendarBooking } from '@/types/calendar';
import BookingModal from '@/components/operator/calendar/BookingModal';
import FullCalendarWrapper, {
  CalendarBookingWithStaff,
  SerializedAvailabilityBlock,
} from '@/components/operator/calendar/FullCalendarWrapper';
import StaffSelector from '@/components/operator/calendar/StaffSelector';
import ServiceFilter from '@/components/operator/calendar/ServiceFilter';
import ViewModeToggle, { CalendarViewMode } from '@/components/operator/calendar/ViewModeToggle';
import StaffColumnCalendar from '@/components/operator/calendar/StaffColumnCalendar';
import AvailabilityBlockEditor from '@/components/operator/calendar/AvailabilityBlockEditor';
import { Card, CardContent } from '@/components/ui/card';
import { AVAILABILITY_BLOCK_COLORS } from '@/types/staff-calendar';
import {
  useStaffData,
  useServicesData,
  useBookingsData,
  useAvailabilityData,
  calculateDurationMinutes,
} from '@/hooks/calendar-hooks';

/**
 * Transform client booking format to component booking format for BookingModal
 */
function transformBookingForModal(booking: CalendarBookingWithStaff): CalendarBooking {
  return {
    id: booking.id,
    businessId: booking.businessId,
    businessName: booking.businessName || '',
    serviceId: booking.serviceId,
    serviceName: booking.serviceName,
    customerId: booking.customerId,
    customerName: booking.customerName,
    customerPhone: booking.customerPhone,
    scheduledAt: new Date(booking.scheduledAt),
    endsAt: new Date(booking.endsAt),
    durationMinutes: calculateDurationMinutes(booking.scheduledAt, booking.endsAt),
    status: booking.status as any,
    paymentStatus: booking.paymentStatus as any,
    notes: booking.notes,
    staffId: booking.staffId,
    staffName: booking.staffName,
  };
}

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <Card>
      <CardContent className="flex items-center justify-center p-10">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </CardContent>
    </Card>
  );
}

/**
 * Calendar Page Component
 */
function CalendarPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  // Filter selection state
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<CalendarViewMode>('single');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Modal state
  const [selectedBooking, setSelectedBooking] = useState<CalendarBooking | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Availability editor state
  const [isAvailabilityEditorOpen, setIsAvailabilityEditorOpen] = useState(false);
  const [selectedAvailabilityBlock, setSelectedAvailabilityBlock] = useState<SerializedAvailabilityBlock | null>(null);
  const [preselectedDate, setPreselectedDate] = useState<Date | undefined>(undefined);
  const [preselectedStaffId, setPreselectedStaffId] = useState<string | undefined>(undefined);

  // Use custom hooks for data fetching
  const { staff, staffLoading } = useStaffData(!!operator, selectedBusinessId);
  const { services, setServices, servicesLoading } = useServicesData(!!operator, selectedBusinessId);
  const { bookings, loading, error } = useBookingsData(
    !!operator,
    selectedBusinessId,
    selectedStaffIds,
    selectedServiceIds,
    services,
    setServices
  );
  const {
    availabilityBlocks,
    showAvailability,
    toggleAvailabilityVisibility,
    saveAvailabilityBlock,
  } = useAvailabilityData(!!operator, selectedBusinessId, selectedStaffIds, staff);

  // Reset selections when business changes
  useEffect(() => {
    setSelectedStaffIds([]);
    setSelectedServiceIds([]);
    setViewMode('single');
  }, [selectedBusinessId]);

  // Handle booking click - open modal with booking details
  const handleBookingClick = useCallback(
    (bookingId: string) => {
      const booking = bookings.find((b: CalendarBookingWithStaff) => b.id === bookingId);
      if (!booking) return;

      setSelectedBooking(transformBookingForModal(booking));
      setIsModalOpen(true);
    },
    [bookings]
  );

  // Handle date click
  const handleDateClick = useCallback((date: Date) => {
    console.log('Date clicked:', date);
  }, []);

  // Handle date click with staff context
  const handleDateClickWithStaff = useCallback((date: Date, staffId: string) => {
    console.log('Date clicked:', date, 'for staff:', staffId);
  }, []);

  // Handle date change
  const handleDateChange = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  }, []);

  // Handle view customer profile
  const handleViewCustomer = useCallback((customerId: string) => {
    navigate({ to: `/operator/customers/${customerId}` });
    setIsModalOpen(false);
    setSelectedBooking(null);
  }, [navigate]);

  // Handle staff selection change
  const handleStaffChange = useCallback((staffIds: string[]) => {
    setSelectedStaffIds(staffIds);
  }, []);

  // Handle service selection change
  const handleServiceChange = useCallback((serviceIds: string[]) => {
    setSelectedServiceIds(serviceIds);
  }, []);

  // Handle view mode change
  const handleViewModeChange = useCallback((mode: CalendarViewMode) => {
    setViewMode(mode);
    const isMultiStaffView = mode === 'multi-column' || mode === 'overlay';

    if (isMultiStaffView && selectedStaffIds.length === 0 && staff.length > 0) {
      setSelectedStaffIds(staff.map((s: { id: string }) => s.id));
    }
  }, [selectedStaffIds.length, staff]);

  // Handle availability block click
  const handleAvailabilityClick = useCallback((block: SerializedAvailabilityBlock) => {
    setSelectedAvailabilityBlock(block);
    setIsAvailabilityEditorOpen(true);
  }, []);

  // Handle add availability block
  const handleAddAvailabilityBlock = useCallback(() => {
    setSelectedAvailabilityBlock(null);
    setPreselectedDate(undefined);
    setPreselectedStaffId(selectedStaffIds.length === 1 ? selectedStaffIds[0] : undefined);
    setIsAvailabilityEditorOpen(true);
  }, [selectedStaffIds]);

  // Handle availability editor close
  const handleAvailabilityEditorClose = useCallback(() => {
    setIsAvailabilityEditorOpen(false);
    setSelectedAvailabilityBlock(null);
    setPreselectedDate(undefined);
    setPreselectedStaffId(undefined);
  }, []);

  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!operator) {
    return null;
  }

  // Calculate filtered booking count
  const hasStaffFilter = selectedStaffIds.length > 0;
  const filteredBookingCount = hasStaffFilter
    ? bookings.filter((b: CalendarBookingWithStaff) => !b.staffId || selectedStaffIds.includes(b.staffId)).length
    : bookings.length;

  const hasSelectedBusiness = !!selectedBusinessId;
  const hasStaff = staff.length > 0;
  const hasServices = services.length > 0;
  const hasSelectedStaff = selectedStaffIds.length > 0;
  const hasSelectedServices = selectedServiceIds.length > 0;
  const hasAvailabilityBlocks = availabilityBlocks.length > 0;
  const showLegend = (hasSelectedStaff || hasAvailabilityBlocks) && hasStaff;
  const isSingleOrOverlayView = viewMode === 'single' || viewMode === 'overlay';

  const editBlock = selectedAvailabilityBlock
    ? {
        ...selectedAvailabilityBlock,
        start: new Date(selectedAvailabilityBlock.start),
        end: new Date(selectedAvailabilityBlock.end),
        display: 'background' as const,
      }
    : undefined;

  return (
    <div className="p-4 space-y-4">
      {/* Page Header */}
      <CalendarPageHeader
        title={t('calendar.title')}
        subtitle={`${selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')} - ${t('calendar.bookingCount', { count: filteredBookingCount })}`}
        hasSelectedBusiness={hasSelectedBusiness}
        hasStaff={hasStaff}
        hasServices={hasServices}
        staff={staff}
        services={services}
        selectedStaffIds={selectedStaffIds}
        selectedServiceIds={selectedServiceIds}
        viewMode={viewMode}
        staffLoading={staffLoading}
        servicesLoading={servicesLoading}
        onStaffChange={handleStaffChange}
        onServiceChange={handleServiceChange}
        onViewModeChange={handleViewModeChange}
        onAddAvailabilityBlock={handleAddAvailabilityBlock}
        t={t}
      />

      {/* Staff and Availability Legend */}
      {showLegend && (
        <LegendBar
          staff={staff}
          selectedStaffIds={selectedStaffIds}
          hasSelectedStaff={hasSelectedStaff}
          hasAvailabilityBlocks={hasAvailabilityBlocks}
          showAvailability={showAvailability}
          toggleAvailabilityVisibility={toggleAvailabilityVisibility}
          t={t}
        />
      )}

      {/* Service Filter Info */}
      {hasSelectedServices && hasServices && (
        <ServiceFilterInfo services={services} selectedServiceIds={selectedServiceIds} />
      )}

      {/* Error Display */}
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Calendar View */}
      {loading ? (
        <LoadingSpinner />
      ) : isSingleOrOverlayView ? (
        <FullCalendarWrapper
          bookings={bookings}
          onBookingClick={handleBookingClick}
          onDateClick={handleDateClick}
          staff={staff}
          selectedStaffIds={selectedStaffIds}
          showStaffName={true}
          colorMode="staff"
          viewMode={viewMode}
          availabilityBlocks={availabilityBlocks}
          showAvailability={showAvailability}
          onAvailabilityClick={handleAvailabilityClick}
        />
      ) : (
        <StaffColumnCalendar
          staff={staff}
          selectedStaffIds={selectedStaffIds}
          bookings={bookings}
          onBookingClick={handleBookingClick}
          onDateClick={handleDateClickWithStaff}
          currentDate={currentDate}
          onDateChange={handleDateChange}
        />
      )}

      {/* Booking Details Modal */}
      <BookingModal
        booking={selectedBooking}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onViewCustomer={handleViewCustomer}
      />

      {/* Availability Block Editor Modal */}
      <AvailabilityBlockEditor
        isOpen={isAvailabilityEditorOpen}
        onClose={handleAvailabilityEditorClose}
        onSave={saveAvailabilityBlock}
        staff={staff}
        preselectedStaffId={preselectedStaffId}
        preselectedDate={preselectedDate}
        editBlock={editBlock}
      />
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function CalendarPageHeader({
  title,
  subtitle,
  hasSelectedBusiness,
  hasStaff,
  hasServices,
  staff,
  services,
  selectedStaffIds,
  selectedServiceIds,
  viewMode,
  staffLoading,
  servicesLoading,
  onStaffChange,
  onServiceChange,
  onViewModeChange,
  onAddAvailabilityBlock,
  t,
}: {
  title: string;
  subtitle: string;
  hasSelectedBusiness: boolean;
  hasStaff: boolean;
  hasServices: boolean;
  staff: ReturnType<typeof useStaffData>['staff'];
  services: ReturnType<typeof useServicesData>['services'];
  selectedStaffIds: string[];
  selectedServiceIds: string[];
  viewMode: CalendarViewMode;
  staffLoading: boolean;
  servicesLoading: boolean;
  onStaffChange: (ids: string[]) => void;
  onServiceChange: (ids: string[]) => void;
  onViewModeChange: (mode: CalendarViewMode) => void;
  onAddAvailabilityBlock: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {hasSelectedBusiness && (
          <StaffSelector
            staff={staff}
            selectedStaffIds={selectedStaffIds}
            onStaffChange={onStaffChange}
            multiSelect={true}
            placeholder={t('common.allStaff', { defaultValue: 'All Staff' })}
            loading={staffLoading}
          />
        )}

        {hasSelectedBusiness && hasServices && (
          <ServiceFilter
            services={services}
            selectedIds={selectedServiceIds}
            onSelectionChange={onServiceChange}
            loading={servicesLoading}
          />
        )}

        <ViewModeToggle
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          multiStaffEnabled={hasSelectedBusiness && hasStaff}
          disabled={false}
        />

        {hasSelectedBusiness && hasStaff && (
          <button
            onClick={onAddAvailabilityBlock}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Time Off
          </button>
        )}
      </div>
    </div>
  );
}

function LegendBar({
  staff,
  selectedStaffIds,
  hasSelectedStaff,
  hasAvailabilityBlocks,
  showAvailability,
  toggleAvailabilityVisibility,
  t,
}: {
  staff: ReturnType<typeof useStaffData>['staff'];
  selectedStaffIds: string[];
  hasSelectedStaff: boolean;
  hasAvailabilityBlocks: boolean;
  showAvailability: boolean;
  toggleAvailabilityVisibility: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const staffColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      {hasSelectedStaff && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {t('common.staffLegend') || 'Staff:'}
          </span>
          {staff
            .filter((s: { id: string }) => selectedStaffIds.includes(s.id))
            .map((s: { id: string; name: string; color?: string }) => {
              const originalIndex = staff.findIndex((st: { id: string }) => st.id === s.id);
              const color = s.color || staffColors[originalIndex % 8];
              return (
                <div key={s.id} className="flex items-center gap-2">
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: color,
                    }}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{s.name}</span>
                </div>
              );
            })}
        </div>
      )}

      {hasSelectedStaff && hasAvailabilityBlocks && (
        <div className="hidden sm:block w-px h-6 bg-gray-300 dark:bg-gray-600" />
      )}

      {hasAvailabilityBlocks && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={toggleAvailabilityVisibility}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Availability:
          </button>
          {Object.entries(AVAILABILITY_BLOCK_COLORS).map(([type, colors]) => (
            <div key={type} className="flex items-center gap-1.5">
              <span
                className="w-3 h-3 rounded"
                style={{
                  backgroundColor: colors.hex,
                  opacity: showAvailability ? 1 : 0.4,
                }}
              />
              <span
                className="text-xs text-gray-600 dark:text-gray-400"
                style={{ opacity: showAvailability ? 1 : 0.6 }}
              >
                {colors.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceFilterInfo({ services, selectedServiceIds }: {
  services: ReturnType<typeof useServicesData>['services'];
  selectedServiceIds: string[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
      <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
        Filtered Services:
      </span>
      {services
        .filter((s: { id: string }) => selectedServiceIds.includes(s.id))
        .map((s: { id: string; name: string }) => (
          <div key={s.id} className="flex items-center gap-2">
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
              }}
            />
            <span className="text-sm text-emerald-700 dark:text-emerald-300">{s.name}</span>
          </div>
        ))}
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/calendar')({
  component: CalendarPage,
});
